/**
 * Copyright (c) 2025 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { UserRole } from './roles'

// ============================================================================
// ENTITIES
// ============================================================================

export const Entity = {
  ALBUM: 'album',
  CHAT: 'chat',
  COMMENT: 'comment',
  EVENT: 'event',
  GROUP: 'group',
  MESSAGE: 'message',
  MEDIA: 'media',
  PLACE: 'place',
  PUBLICATION: 'publication',
  POST: 'post',
  PROFILE: 'profile',
  ADMIN: 'admin',
  REVIEW: 'review',
  RENDEZVOUS: 'rendezvous',
  LOCATION: 'location',
  VENDOR_MARKET: 'vendor-market',
} as const

export type Entity = (typeof Entity)[keyof typeof Entity]

// ============================================================================
// ACTIONS
// ============================================================================

/** Standard CRUD actions (owner-level access) */
type StandardAction = 'read' | 'create' | 'update' | 'delete'

/** Admin actions (system-level access to all resources) */
type AdminAction = 'read:all' | 'create:all' | 'update:all' | 'delete:all'

// ============================================================================
// ENTITY-SPECIFIC ACTIONS
// ============================================================================

type EventSpecificAction =
  | 'change-location'
  | 'transfer'
  | 'transfer:all'
  | 'attend'
  | 'attend:all'
  | 'invite'
  | 'invite:all'
  | 'post'
  | 'post:all'
  | 'delete-post'
  | 'delete-post:all'

type GroupSpecificAction =
  | 'post'
  | 'post:all'
  | 'delete-post'
  | 'delete-post:all'
  | 'create-event'
  | 'create-event:all'
  | 'moderate'
  | 'moderate:all'
  | 'invite'
  | 'invite:all'

/**
 * `create-channel` gates public/private channels behind supporter, while every member
 * can still start DMs and groups via `create`. `moderate` covers deleting other
 * people's messages and removing members within a room you administer.
 */
type ChatSpecificAction =
  | 'create-channel'
  | 'create-channel:all'
  | 'invite'
  | 'invite:all'
  | 'moderate'
  | 'moderate:all'
  | 'react'
  | 'upload-media'

type MediaSpecificAction = 'upload-video' | 'view-video' | 'review' | 'edit-image'

type PlaceSpecificAction = 'review' | 'transfer:all'

type VendorMarketSpecificAction = 'review' | 'transfer:all'

type PublicationSpecificAction = 'review' | 'transfer:all'

type PostSpecificAction = 'review' | 'view-trending'

type ProfileSpecificAction = 'see-views' | 'see-likes' | 'beta-program' | 'verification:all'

type AdminPanelAction =
  | 'access'
  | 'accounts'
  | 'reports'
  | 'blocks'
  | 'deleted-accounts'
  | 'job-trigger'
  | 'review-media'
  | 'fatal-error'
  | 'payment-requests'
  | 'points'

type LocationSpecificAction = 'view-map' | 'change-location'

// ============================================================================
// PERMISSION TYPES
// ============================================================================

/** Entity-specific permissions */
type AlbumPermission = `album:${StandardAction | AdminAction}`
type ChatPermission = `chat:${StandardAction | AdminAction | ChatSpecificAction}`
type CommentPermission = `comment:${StandardAction | AdminAction}`
type EventPermission = `event:${StandardAction | AdminAction | EventSpecificAction}`
type GroupPermission = `group:${StandardAction | AdminAction | GroupSpecificAction}`
type MessagePermission = `message:${StandardAction | AdminAction}`
type MediaPermission = `media:${StandardAction | AdminAction | MediaSpecificAction}`
type PlacePermission = `place:${StandardAction | AdminAction | PlaceSpecificAction}`
type VendorMarketPermission = `vendor-market:${StandardAction | AdminAction | VendorMarketSpecificAction}`
type PublicationPermission = `publication:${StandardAction | AdminAction | PublicationSpecificAction}`
type PostPermission = `post:${StandardAction | AdminAction | PostSpecificAction}`
type ProfilePermission = `profile:${StandardAction | AdminAction | ProfileSpecificAction}`
type AdminPermission = `admin:${AdminPanelAction}`
type ReviewPermission = `review:${StandardAction | AdminAction}`
type RendezvousPermission = `rendezvous:${StandardAction | AdminAction}`
type LocationPermission = `location:${LocationSpecificAction}`

/** Full Permission type - union of all possible permissions */
export type Permission =
  | AlbumPermission
  | ChatPermission
  | CommentPermission
  | EventPermission
  | GroupPermission
  | MessagePermission
  | MediaPermission
  | PlacePermission
  | VendorMarketPermission
  | PublicationPermission
  | PostPermission
  | ProfilePermission
  | AdminPermission
  | ReviewPermission
  | RendezvousPermission
  | LocationPermission

// ============================================================================
// PERMISSION SETS BY ROLE
// ============================================================================

/** Unauthenticated permissions */
const UNAUTH_PERMISSIONS: Permission[] = [
  // Album permissions
  'album:read',
  // Comment permissions
  'comment:read',
  // Event Permissions
  'event:read',
  // Group Permissions
  'group:read',
  // Media Permissions
  'media:read',
  // Place Permissions
  'place:read',
  // Vendor market permissions
  'vendor-market:read',
  // Publication Permissions
  'publication:read',
  // Profile Permissions
  'profile:read',
  // Review Permissions
  'review:read',
  // Rendezvous Permissions
  'rendezvous:read',
]

/** Base permissions available to all authenticated users */
const MEMBER_PERMISSIONS: Permission[] = [
  // Album permissions
  'album:read',
  'album:create',
  'album:update',
  'album:delete',
  // Chat Permissions
  'chat:read',
  'chat:create',
  'chat:update',
  'chat:delete',
  'chat:invite',
  'chat:react',
  'chat:upload-media',
  // Comment permissions
  'comment:read',
  'comment:create',
  'comment:update',
  'comment:delete',
  // Event Permissions
  'event:read',
  'event:create',
  'event:update',
  'event:delete',
  'event:attend',
  'event:invite',
  'event:post',
  'event:delete-post',
  // Group Permissions
  'group:read',
  'group:post',
  'group:invite',
  // Message Permissions
  'message:read',
  'message:create',
  'message:update',
  'message:delete',
  // Media Permissions
  'media:view-video', // Allow all users to view videos for now
  'media:upload-video', // Allow all users to upload videos for now
  'media:read',
  'media:create',
  'media:update',
  'media:delete',
  // Place Permissions
  'place:read',
  // Vendor market permissions
  'vendor-market:read',
  'vendor-market:create',
  // Publication Permissions
  'publication:read',
  // Post Permissions
  'post:read',
  'post:create',
  'post:update',
  'post:delete',
  // Profile Permissions
  'profile:read',
  'profile:update',
  // Review Permissions
  'review:read',
  'review:create',
  'review:update',
  'review:delete',
  // Rendezvous Permissions
  'rendezvous:read',
  'rendezvous:create',
  'rendezvous:update',
  'rendezvous:delete',
]

/** Standard user permissions (Preview/Supporter level) */
const PREVIEW_USER_PERMISSIONS: Permission[] = [
  ...MEMBER_PERMISSIONS,
  // Media features
  'media:upload-video',
  'media:view-video',
  'media:edit-image',
  // Vendor market features
  'vendor-market:create',
  'vendor-market:update',
  'vendor-market:delete',
  // Publication features
  'publication:create',
  'publication:update',
  'publication:delete',
  // Location features
  'location:view-map',
  'location:change-location',
]

/** Standard user permissions (Preview/Supporter level) */
const SUPPORTER_USER_PERMISSIONS: Permission[] = [
  ...PREVIEW_USER_PERMISSIONS,
  // Group features
  'group:create-event',
  'group:update',
  'group:delete',
  'group:create',
  // Chat features
  'chat:create-channel',
  // Place features
  'place:create',
  'place:update',
  'place:delete',
  // Supporters can see their own profile analytics
  'profile:see-views',
  'profile:see-likes',
  'profile:beta-program',
]

/** Supporter-level viewing without creating or editing items (tradeshow kiosk accounts). */
const TRADESHOW_USER_PERMISSIONS: Permission[] = [
  // Album permissions
  'album:read',
  // Chat Permissions
  'chat:read',
  // Comment permissions
  'comment:read',
  // Event Permissions
  'event:read',
  // Group Permissions
  'group:read',
  // Message Permissions
  'message:read',
  // Media Permissions
  'media:read',
  'media:view-video',
  // Place Permissions
  'place:read',
  // Vendor market permissions
  'vendor-market:read',
  // Publication Permissions
  'publication:read',
  // Post Permissions
  'post:read',
  // Profile Permissions
  'profile:read',
  'profile:see-views',
  'profile:see-likes',
  'profile:beta-program',
  // Review Permissions
  'review:read',
  // Rendezvous Permissions
  'rendezvous:read',
  // Location features
  'location:view-map',
  'location:change-location',
]

/** Moderator-specific permissions */
const MODERATOR_PERMISSIONS: Permission[] = [
  ...SUPPORTER_USER_PERMISSIONS,
  // Admin panel access
  'admin:access',
  'admin:review-media',
  // Post analytics
  'post:view-trending',
  // Chat moderation across every room
  'chat:moderate:all',
  // Review capabilities
  'media:review',
  'place:review',
  'vendor-market:review',
  'post:review',
  'publication:review',
]

/** Admin permissions - full access to everything */
const ADMIN_PERMISSIONS: Permission[] = [
  ...MODERATOR_PERMISSIONS,
  // Full admin panel access
  'admin:accounts',
  'admin:reports',
  'admin:blocks',
  'admin:deleted-accounts',
  'admin:job-trigger',
  'admin:payment-requests',
  'admin:fatal-error',
  'admin:points',
  // Profile management
  'profile:verification:all',
  // Album Admin Permissions
  'album:read:all',
  'album:create:all',
  'album:update:all',
  'album:delete:all',
  // Chat Admin Permissions
  'chat:read:all',
  'chat:create:all',
  'chat:update:all',
  'chat:delete:all',
  'chat:create-channel',
  'chat:create-channel:all',
  'chat:invite:all',
  // Comment Admin Permissions
  'comment:read:all',
  'comment:create:all',
  'comment:update:all',
  'comment:delete:all',
  // Event Admin Permissions
  'event:read:all',
  'event:create:all',
  'event:update:all',
  'event:delete:all',
  'event:transfer:all',
  'event:attend:all',
  'event:invite:all',
  'event:post:all',
  'event:delete-post:all',
  // Group Admin Permissions
  'group:read:all',
  'group:create:all',
  'group:update:all',
  'group:delete:all',
  'group:post:all',
  'group:delete-post:all',
  'group:create-event:all',
  'group:moderate:all',
  'group:invite:all',
  // Message Admin Permissions
  'message:read:all',
  'message:create:all',
  'message:update:all',
  'message:delete:all',
  // Media Admin Permissions
  'media:read:all',
  'media:create:all',
  'media:update:all',
  'media:delete:all',
  // Place Admin Permissions
  'place:read:all',
  'place:create:all',
  'place:update:all',
  'place:delete:all',
  'place:transfer:all',
  // Vendor market admin permissions
  'vendor-market:read:all',
  'vendor-market:create:all',
  'vendor-market:update:all',
  'vendor-market:delete:all',
  'vendor-market:transfer:all',
  // Publication Admin Permissions
  'publication:read:all',
  'publication:create:all',
  'publication:update:all',
  'publication:delete:all',
  'publication:transfer:all',
  // Post Admin Permissions
  'post:read:all',
  'post:create:all',
  'post:update:all',
  'post:delete:all',
  // Profile Admin Permissions
  'profile:read:all',
  'profile:create:all',
  'profile:update:all',
  'profile:delete:all',
  // Review Admin Permissions
  'review:read:all',
  'review:create:all',
  'review:update:all',
  'review:delete:all',
  // Rendezvous Admin Permissions
  'rendezvous:read:all',
  'rendezvous:create:all',
  'rendezvous:update:all',
  'rendezvous:delete:all',
]

// ============================================================================
// ROLE PERMISSION CONFIGURATION
// ============================================================================

export const ROLE_PERMISSIONS: Record<UserRole, readonly Permission[]> = {
  [UserRole.UNAUTH]: UNAUTH_PERMISSIONS,
  [UserRole.MEMBER]: MEMBER_PERMISSIONS,
  [UserRole.PREVIEW]: PREVIEW_USER_PERMISSIONS,
  [UserRole.SUPPORTER]: SUPPORTER_USER_PERMISSIONS,
  [UserRole.TRADESHOW]: TRADESHOW_USER_PERMISSIONS,
  [UserRole.MODERATOR]: MODERATOR_PERMISSIONS,
  [UserRole.ADMIN]: ADMIN_PERMISSIONS,
}

// ============================================================================
// EFFECTIVE ROLE (assume-role for staff)
// ============================================================================

/** Resolve the role used for permissions and feature flags when staff may assume another role. */
export function effectiveUserRole(user: { roles: string | null; assumeRoles: string | null }): UserRole {
  const roles = user.roles ?? UserRole.MEMBER
  const canAssumeRole = roles.includes('admin') || roles.includes('moderator')
  let finalRole = roles as UserRole
  if (user.assumeRoles && canAssumeRole) {
    finalRole = user.assumeRoles as UserRole
  }
  return finalRole
}

// ============================================================================
// PERMISSION CHECK UTILITIES
// ============================================================================

/** Check if a role has a specific permission */
export function hasPermission(role: UserRole, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role ?? UserRole.UNAUTH].includes(permission)
}

/** Check if a role has any of the specified permissions */
export function hasAnyPermission(role: UserRole, permissions: Permission[]): boolean {
  const rolePermissions = ROLE_PERMISSIONS[role ?? UserRole.UNAUTH]
  return permissions.some((p) => rolePermissions.includes(p))
}

/** Check if a role has all of the specified permissions */
export function hasAllPermissions(role: UserRole, permissions: Permission[]): boolean {
  const rolePermissions = ROLE_PERMISSIONS[role ?? UserRole.UNAUTH]
  return permissions.every((p) => rolePermissions.includes(p))
}

/** Get all permissions for a role */
export function getPermissions(role: UserRole): readonly Permission[] {
  return ROLE_PERMISSIONS[role ?? UserRole.UNAUTH]
}

// ============================================================================
// PERMISSION PARSING UTILITIES
// ============================================================================

export interface ParsedPermission {
  entity: string
  action: string
  scope: 'own' | 'all'
}

/** Parse a permission string into its components */
export function parsePermission(permission: Permission): ParsedPermission {
  const parts = permission.split(':')
  const entity = parts[0] ?? ''
  const isAllScope = parts[parts.length - 1] === 'all'

  let action: string
  if (isAllScope && parts.length > 2) {
    action = parts.slice(1, -1).join(':')
  } else if (isAllScope) {
    action = 'all'
  } else {
    action = parts.slice(1).join(':')
  }

  return {
    entity,
    action,
    scope: isAllScope ? 'all' : 'own',
  }
}

/** Check if a permission grants access to all resources (admin-level) */
export function isAdminPermission(permission: Permission): boolean {
  return permission.endsWith(':all')
}

/** Get all permissions for an entity */
export function getEntityPermissions(role: UserRole, entity: Entity): Permission[] {
  return ROLE_PERMISSIONS[role ?? UserRole.UNAUTH].filter((p) => p.startsWith(`${entity}:`)) as Permission[]
}
